/**
 * Team Users management panel — Orange HQ Settings → Team & Users.
 *
 * Columns: NAME · USERNAME · EMAIL · ROLE · ACTIVE · LAST LOGIN.
 * Actions per row (owner row protected): Edit / Reset password /
 * Activate-Deactivate / Delete-Archive.
 *
 * All backend calls hit /api/hq/control/team/users; authorization is enforced
 * server-side so nothing here is a security boundary.
 */
import React, { useEffect, useState } from "react";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton } from "@/hq/HQPrimitives";
import { toast } from "sonner";
import {
  Plus, Pencil, Key, ToggleRight, ToggleLeft, Trash2, ShieldCheck, X,
} from "lucide-react";

const ROLE_STYLE = {
  owner: "bg-[#0A0A0A] text-white",
  admin: "bg-[#FF6A00] text-black",
  sales: "bg-[#FFE0BF] text-[#8A4C00]",
};
const ROLE_LABEL = { owner: "OWNER", admin: "ADMIN", sales: "SALES" };

function Field({ label, children, testId, hint }) {
  return (
    <label className="block" data-testid={testId}>
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">{label}</div>
      {children}
      {hint && <div className="mt-1 text-[11px] text-[#8A8A8A]">{hint}</div>}
    </label>
  );
}
const inputCls =
  "w-full px-3 py-2 rounded-lg border border-[#EAEAEA] bg-white text-[#0A0A0A] " +
  "placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#FF6A00] text-[14px]";

function fmt(dt) {
  if (!dt) return "Never";
  try {
    const d = new Date(dt);
    return d.toLocaleDateString("en-GB") + " " +
      d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

function CreateEditModal({ open, mode, user, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    full_name: "", username: "", email: "", role: "sales", password: "", active: true,
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(isEdit ? {
      full_name: user.full_name || "", username: user.username || "",
      email: user.email || "", role: user.role || "sales",
      password: "", active: user.active !== false,
    } : { full_name: "", username: "", email: "", role: "sales", password: "", active: true });
  }, [open, mode, user, isEdit]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const body = {
          full_name: form.full_name,
          username: form.username,
          email: form.email,
          active: form.active,
        };
        // OWNER role cannot be reassigned through this UI — only send role
        // when the target is not the owner.
        if (user.role !== "owner") body.role = form.role;
        const r = await hqApi.patch(`/control/team/users/${user.id}`, body);
        toast.success("User updated");
        onSaved(r.data.user);
      } else {
        const r = await hqApi.post("/control/team/users", form);
        toast.success("User created");
        onSaved(r.data.user);
      }
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="hq-user-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Team &amp; users</div>
            <h3 className="mt-1 font-black text-xl">{isEdit ? "Edit user" : "Create user"}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F6F5F2]" data-testid="hq-user-modal-close"><X size={16}/></button>
        </div>
        <form onSubmit={submit} className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Full name" testId="hq-user-field-name">
            <input required className={inputCls} value={form.full_name}
                   onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                   placeholder="Nathan Ritchie"/>
          </Field>
          <Field label="Username" testId="hq-user-field-username" hint="Lowercase, 2-32 chars">
            <input required className={inputCls} value={form.username}
                   onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
                   placeholder="nathan"/>
          </Field>
          <Field label="Email" testId="hq-user-field-email">
            <input required type="email" className={inputCls} value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   placeholder="name@orangebysite.com"/>
          </Field>
          <Field label="Role" testId="hq-user-field-role">
            {user?.role === "owner" && isEdit ? (
              <div className={`px-3 py-2 rounded-lg bg-[#F6F5F2] font-black text-[12px] tracking-[0.16em]`}>OWNER</div>
            ) : (
              <select className={inputCls} value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="admin">ADMIN</option>
                <option value="sales">SALES</option>
              </select>
            )}
          </Field>
          {!isEdit && (
            <div className="md:col-span-2">
              <Field label="Temporary password" testId="hq-user-field-password" hint="Minimum 10 characters. User can change on next login.">
                <input required type="password" className={inputCls} value={form.password}
                       onChange={(e) => setForm({ ...form, password: e.target.value })}
                       placeholder="•••••••••••"/>
              </Field>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm md:col-span-2" data-testid="hq-user-field-active">
            <input type="checkbox" checked={form.active}
                   onChange={(e) => setForm({ ...form, active: e.target.checked })}/>
            <span>Active</span>
            <span className="text-[11px] text-[#8A8A8A]">— inactive users cannot log in and existing sessions are revoked.</span>
          </label>
          <div className="md:col-span-2 flex justify-end gap-2 mt-2">
            <button type="button" onClick={onClose}
                    className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase">Cancel</button>
            <button type="submit" disabled={saving} data-testid="hq-user-modal-save"
                    className="px-5 py-2 rounded-full bg-[#FF6A00] text-black text-[10px] font-black tracking-[0.16em] uppercase disabled:opacity-50">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal({ open, user, onClose }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setPw(""); }, [open]);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await hqApi.post(`/control/team/users/${user.id}/reset-password`, { password: pw });
      toast.success(`Password reset for ${user.email}`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="hq-user-reset-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Reset password</div>
        <h3 className="mt-1 font-black text-xl">Reset password for {user.email}</h3>
        <p className="mt-2 text-sm text-[#4A4A4A]">This will revoke all existing sessions for this user.</p>
        <div className="mt-4">
          <Field label="New temporary password" hint="Minimum 10 characters">
            <input required type="password" minLength={10} className={inputCls} value={pw}
                   onChange={(e) => setPw(e.target.value)} placeholder="•••••••••••"
                   data-testid="hq-user-reset-input"/>
          </Field>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase">Cancel</button>
          <button type="submit" disabled={saving} data-testid="hq-user-reset-submit"
                  className="px-5 py-2 rounded-full bg-[#0A0A0A] text-white text-[10px] font-black tracking-[0.16em] uppercase disabled:opacity-50">
            {saving ? "Resetting…" : "Reset password"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function HQTeamUsers() {
  const [rows, setRows] = useState(null);
  const [current, setCurrent] = useState(null);
  const [modal, setModal] = useState({ open: false, mode: "create", user: null });
  const [resetModal, setResetModal] = useState({ open: false, user: null });

  const refresh = () => hqApi.get("/control/team/users").then((r) => {
    setRows(r.data.items);
    setCurrent(r.data.current_admin);
  }).catch((e) => {
    if (e.response?.status === 403) setRows([]);
    else toast.error(formatApiError(e.response?.data?.detail) || "Failed to load team");
  });
  useEffect(() => { refresh(); }, []);

  const canManage = current && (current.role === "owner" || current.role === "admin");
  const isOwnerActor = current?.role === "owner";

  const onToggleActive = async (u) => {
    try {
      await hqApi.patch(`/control/team/users/${u.id}`, { active: !u.active });
      toast.success(`User ${!u.active ? "activated" : "deactivated"}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };
  const onDelete = async (u) => {
    if (!window.confirm(`Archive user ${u.email}? Their audit trail is preserved.`)) return;
    try {
      await hqApi.delete(`/control/team/users/${u.id}`);
      toast.success(`Archived ${u.email}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Archive failed"); }
  };

  return (
    <div className="space-y-4" data-testid="hq-settings-team">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Team &amp; users</div>
          <p className="text-[13px] text-[#4A4A4A] mt-1">Manage OrangeBySite staff access, roles and passwords.</p>
        </div>
        {canManage && (
          <button
            data-testid="hq-user-create-btn"
            onClick={() => setModal({ open: true, mode: "create", user: null })}
            className="px-4 py-2 rounded-full bg-[#FF6A00] text-black text-[10px] font-black tracking-[0.16em] uppercase"
          >
            <Plus size={13} className="inline mr-1"/> Create user
          </button>
        )}
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.18em] uppercase font-bold text-[#8A8A8A]">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Username</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Active</th>
              <th className="text-left px-4 py-3">Last login</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!rows
              ? [0, 1].map((i) => <tr key={i} className="border-t border-[#EAEAEA]"><td colSpan={7} className="px-4 py-3"><Skeleton h={20}/></td></tr>)
              : rows.length === 0
                ? <tr><td colSpan={7}><EmptyState title="No team members" testId="hq-team-empty"/></td></tr>
                : rows.map((u) => {
                  const isOwnerRow = u.role === "owner";
                  const canEdit = canManage && (isOwnerActor || !isOwnerRow);
                  const canDelete = canEdit && !isOwnerRow && u.id !== current?.id;
                  const canToggle = canEdit && !isOwnerRow && u.id !== current?.id;
                  return (
                    <tr key={u.id} data-testid={`hq-user-row-${u.username || u.id}`} className="border-t border-[#EAEAEA]">
                      <td className="px-4 py-3 font-semibold">
                        {isOwnerRow && <ShieldCheck size={12} className="inline mr-1 text-[#FF6A00]"/>}
                        {u.full_name || "—"}
                      </td>
                      <td className="px-4 py-3 text-[#4A4A4A]">{u.username || <span className="text-[#8A8A8A]">—</span>}</td>
                      <td className="px-4 py-3 text-[#4A4A4A]">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black tracking-[0.14em] ${ROLE_STYLE[u.role] || "bg-[#F6F5F2]"}`} data-testid={`hq-user-role-${u.username || u.id}`}>
                          {ROLE_LABEL[u.role] || (u.role || "").toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3">{u.active === false ? <span className="text-[#C1350B] font-bold">No</span> : "Yes"}</td>
                      <td className="px-4 py-3 text-[#8A8A8A]" data-testid={`hq-user-lastlogin-${u.username || u.id}`}>{fmt(u.last_login_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          {canEdit && (
                            <button data-testid={`hq-user-edit-${u.username || u.id}`}
                                    title="Edit user"
                                    onClick={() => setModal({ open: true, mode: "edit", user: u })}
                                    className="p-2 rounded-full hover:bg-[#F6F5F2]"><Pencil size={14}/></button>
                          )}
                          {canEdit && (
                            <button data-testid={`hq-user-reset-${u.username || u.id}`}
                                    title="Reset password"
                                    onClick={() => setResetModal({ open: true, user: u })}
                                    className="p-2 rounded-full hover:bg-[#F6F5F2]"><Key size={14}/></button>
                          )}
                          {canToggle && (
                            <button data-testid={`hq-user-toggle-${u.username || u.id}`}
                                    title={u.active ? "Deactivate" : "Activate"}
                                    onClick={() => onToggleActive(u)}
                                    className="p-2 rounded-full hover:bg-[#F6F5F2]">
                              {u.active ? <ToggleRight size={16} className="text-[#0FA958]"/> : <ToggleLeft size={16} className="text-[#8A8A8A]"/>}
                            </button>
                          )}
                          {canDelete && (
                            <button data-testid={`hq-user-delete-${u.username || u.id}`}
                                    title="Delete / archive"
                                    onClick={() => onDelete(u)}
                                    className="p-2 rounded-full hover:bg-[#F6F5F2] text-[#C1350B]"><Trash2 size={14}/></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </Card>

      <CreateEditModal
        open={modal.open}
        mode={modal.mode}
        user={modal.user}
        onClose={() => setModal({ ...modal, open: false })}
        onSaved={() => refresh()}
      />
      <ResetPasswordModal
        open={resetModal.open}
        user={resetModal.user || {}}
        onClose={() => setResetModal({ open: false, user: null })}
      />
    </div>
  );
}
